import { TIPOS_PAUSA, motivoPausaSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Campo, Checkbox, Input, Select } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/motivos-pausa')({ component: MotivosPausa })

const ROTULO_TIPO: Record<(typeof TIPOS_PAUSA)[number], string> = {
  PESSOAL: 'Pessoal',
  TECNICA: 'Técnica',
  FALTA_MATERIAL: 'Falta de material',
  MANUTENCAO: 'Manutenção',
  OUTRO: 'Outro',
}

type Item = NonNullable<ReturnType<typeof useMotivos>['data']>[number]
function useMotivos() {
  const trpc = useTRPC()
  return useQuery(trpc.cadastros.motivosPausa.listar.queryOptions())
}

function MotivosPausa() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useMotivos()
  const g = useGavetaCadastro<Item>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.motivosPausa.listar.queryKey() })
  const criar = useMutation(
    trpc.cadastros.motivosPausa.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.motivosPausa.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: motivoPausaSchema,
    numericos: ['ordem'],
    booleanos: ['ativo', 'contaComoProdutivo', 'ajudaOutroPosto', 'fimDoExpediente'],
    setErros: g.setErros,
    sucesso: (d) => `Motivo ${d.nome} salvo.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <>
      <BarraTitulo
        titulo="Motivos de pausa"
        descricao="Botões que o operador vê ao pausar. Pausas 'produtivas' (ex.: limpeza programada) não derrubam a produtividade."
        onNovo={g.abrirNovo}
        rotuloNovo="Novo motivo"
      />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Tipo</Th>
          <Th>Produtiva</Th>
          <Th>Ordem</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((m) => (
            <Linha key={m.id}>
              <Td className="font-medium">
                {m.nome} <span className="ml-1 font-mono text-xs text-fg-subtle">{m.codigo}</span>{' '}
                {!m.ativo && <Ativo ativo={false} />}
              </Td>
              <Td className="text-fg-muted">{ROTULO_TIPO[m.tipo]}</Td>
              <Td>
                {m.contaComoProdutivo ? 'Sim' : '—'}
                {m.ajudaOutroPosto && (
                  <span className="ml-2 text-sm text-fg-muted">com ajuda confirmada</span>
                )}
                {m.fimDoExpediente && (
                  <span className="ml-2 text-sm text-fg-muted">fim do expediente</span>
                )}
              </Td>
              <Td numerico>{m.ordem}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={m.nome} onClick={() => g.abrirEditar(m)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={5} />}
          {lista.data?.length === 0 && <Vazio colunas={5}>Nenhum motivo cadastrado.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar motivo' : 'Novo motivo'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-motivo"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-motivo" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Nome (no botão)" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo
            label="Código"
            erro={g.erros.codigo}
            dica="Minúsculas e _ (ex.: falta_material). Não muda depois de usado."
          >
            {(p) => (
              <Input {...p} name="codigo" defaultValue={g.item?.codigo} className="font-mono" />
            )}
          </Campo>
          <Campo label="Tipo" erro={g.erros.tipo}>
            {(p) => (
              <Select {...p} name="tipo" defaultValue={g.item?.tipo ?? 'PESSOAL'}>
                {TIPOS_PAUSA.map((t) => (
                  <option key={t} value={t}>
                    {ROTULO_TIPO[t]}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo label="Ordem no tablet" erro={g.erros.ordem}>
            {(p) => (
              <Input {...p} name="ordem" type="number" min={0} defaultValue={g.item?.ordem ?? 0} />
            )}
          </Campo>
          <Checkbox
            name="contaComoProdutivo"
            label="Conta como tempo produtivo"
            defaultChecked={g.item?.contaComoProdutivo ?? false}
          />
          <Checkbox
            name="ajudaOutroPosto"
            label="É ajuda a outro posto (só conta com o PIN do ajudante no outro tablet)"
            defaultChecked={g.item?.ajudaOutroPosto ?? false}
          />
          <Checkbox
            name="fimDoExpediente"
            label="É o fim do expediente (aparece só ao sair; fica fora do tempo parado)"
            defaultChecked={g.item?.fimDoExpediente ?? false}
          />
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </>
  )
}
